import { useNavigate } from '@tanstack/react-router';
import { IconAlarm, IconCalendarEvent, IconCertificate, IconClock, IconEye, IconEyeOff, IconFileCheck, IconId, IconLock, IconLogout, IconMapPin, IconPencil, IconPlayerPlay, IconPlus, IconSchool, IconUserCheck, type Icon } from '@tabler/icons-react';
import { useState } from 'react';
import { PageHeader, useLayout } from '@hana/shell';
import { Button, Chip, StateView, useToast } from '@hana/ui';
import { useT } from '@hana/i18n';
import './overview.i18n';
import { dayRecord, leaveBalance, otHours } from '../data';
import { NopermView, OverviewSkeleton, StateCard } from '../meeState';
import { LeaveDonut, MeeHeaderTools, TypeIcon } from '../Parts';
import { RequestDrawer } from '../requests/RequestDrawer';
import { useMee } from '../useMee';
import s from '../mee.module.css';
import { ADVANCE_SAMPLE, TODO_SAMPLE } from './sample';
import { fdw, monthAbbr, num, qtyLabel, rangeLabel, todayCard, upcomingHolidays } from './view';

const TODO_ICON: Record<string, Icon> = { sign: IconPencil, edu: IconSchool, cert: IconCertificate };

function OverviewBody({ m, compact }: { m: ReturnType<typeof useMee>; compact: boolean }) {
  const t = useT();
  const nav = useNavigate();
  const toast = useToast();
  const phone = useLayout() === 'phone';
  const me = m.me!;
  const goLeave = () => void nav({ to: '/mee/leave' });
  const rec = m.attendance.find((a) => a.day === m.today);
  const td = todayCard(m.cal, m.today, m.now, rec);
  const lv = leaveBalance(m.cal, me, m.mine);
  const pend = m.mine.filter((r) => r.status === 'Pending');
  const holidays = upcomingHolidays(m.cal, m.today);
  const teamPend = m.team.filter((r) => r.status === 'Pending').length;
  const rd = dayRecord(m.cal, m.today, m.today, rec);
  const todo = [
    ...(rd.status === 'short' ? [{ k: t('ov.clockMissing'), kind: 'clock', badge: '', tone: 'warn', tip: '' }] : []),
    ...TODO_SAMPLE.filter((x) => !x.mgrOnly || me.manager),
  ].slice(0, phone ? 3 : undefined);
  const [salary, setSalary] = useState(false);

  return (
    <div className={s.page} data-compact={compact ? '1' : undefined}>
      {me.manager && (
        <button type="button" className={s.mgr} onClick={() => void nav({ to: '/mee/approve' })}>
          <IconUserCheck size={20} stroke={1.8} aria-hidden="true" />
          <b>{t('ov.teamPend', { n: teamPend })}</b>
          {!phone && <span>[{me.seats[0]![0]}] {me.id}</span>}
          <span className={s.go}>{phone ? '→' : t('ov.approve')}</span>
        </button>
      )}
      <div className={s.rowTop}>
        <section className={s.card} aria-label={t('ov.todayAria')}>
          <div className={s.head}>
            <b>{t('ov.today')} · {fdw(m.today)}</b>
            {!td.off && (
              <Chip icon={<IconClock size={14} />} label={t('ov.shiftLb')}>
                {td.shift}
              </Chip>
            )}
            <span className={s.grow} />
            <Chip tone="ok" icon={<IconMapPin size={14} />} label={t('ov.inRadius')}>
              {t('ov.office', { d: 38 })}
            </Chip>
          </div>
          {td.off ? (
            <div className={s.none}>{t('ov.offDay')}</div>
          ) : (
            <>
              <div className={s.bar} role="img" aria-label={t('ov.shiftAria', { s: td.shift, p: Math.round(td.nowPct) })}>
                {td.segs.map((g, i) => (
                  <div key={i} className={`${s.seg} ${g.kind === 'break' ? s.brk : ''}`} style={{ width: `${g.pct}%` }} />
                ))}
                <i className={s.now} style={{ left: `${td.nowPct}%` }} />
              </div>
              <div className={s.ticks}>
                {td.marks.map((k, i) => (
                  <span key={`${k.t}${i}`} style={{ left: `${k.pct}%` }} data-end={i === td.marks.length - 1 ? '1' : undefined}>{k.t}</span>
                ))}
              </div>
              <div className={`${s.kv} ${s.act}`}>
                <div className={s.tile}><span>{t('ov.in')}</span><b>{td.in || '—'}</b></div>
                <div className={s.tile}><span>{t('ov.out')}</span><b>{td.out || '—'}</b></div>
                <div className={s.tile} title={t('ov.presTitle')}><span>{t('ov.pres')}</span><b>{num(td.presence)} h</b></div>
                <Button
                  variant="primary"
                  icon={<IconLogout size={18} />}
                  disabled={!!td.out}
                  onClick={() => toast({ msg: t('ov.clockedOut', { h: m.now }), undo: m.clockOut(m.now) })}
                >
                  {td.out ? t('ov.clockedOut', { h: td.out }) : t('ov.clockOut')}
                </Button>
                {phone && (
                  <Button icon={<IconMapPin size={18} />} onClick={() => toast({ msg: t('ov.faceToast') })}>
                    {t('ov.faceBtn')}
                  </Button>
                )}
              </div>
            </>
          )}
        </section>

        <button type="button" className={s.card} onClick={goLeave} aria-label={t('ov.leaveAria', { l: num(lv.left), q: lv.quota, p: num(lv.pending) })}>
          <div className={s.head}>
            <b>{t('ov.leave')}</b>
          </div>
          <div className={s.donutRow}>
            <LeaveDonut {...lv} />
            <div className={s.legend}>
              <span><i className={s.dot} style={{ background: 'var(--d-gray)' }} />{t('ov.left')} <b>{num(lv.leftAfter)}</b></span>
              <span><i className={s.dot} style={{ background: 'var(--d-amber)' }} />{t('ov.pending')} <b>{num(lv.pending)}</b></span>
              <span><i className={s.dot} style={{ background: 'var(--pri)' }} />{t('ov.used')} <b>{num(lv.used)}</b></span>
            </div>
          </div>
        </button>

        <section className={s.card} aria-label={t('ov.slipAria')}>
          <div className={s.head}>
            <b>{t('ov.salary', { m: '09/2026' })}</b>
            <span className={s.grow} />
            <IconLock size={16} stroke={1.8} aria-label={t('ov.onlyYou')} />
          </div>
          <div className={s.salLb}>{t('ov.net')}</div>
          {/* TODO: số lương thật khi có dữ liệu lương (luật luồng B: chưa hiện số tiền lương) */}
          <div className={s.salVal}>{salary ? t('ov.noPay') : '••••••••• ₫'}</div>
          <div className={s.salBar} />
          <div className={s.salAct}>
            <Button icon={salary ? <IconEyeOff size={18} /> : <IconEye size={18} />} onClick={() => setSalary((v) => !v)}>
              {salary ? t('ov.hide') : t('ov.show')}
            </Button>
            {!phone && (
              <button type="button" className={s.link} onClick={() => toast({ msg: t('ov.noSlip') })}>
                <IconFileCheck size={16} stroke={1.8} aria-hidden="true" />
                {t('ov.viewSlip')}
              </button>
            )}
          </div>
        </section>
      </div>

      <div className={s.rowMid}>
        {!phone && <section className={s.card} aria-label={t('ov.pendReq')}>
          <div className={s.head}>
            <b>{t('ov.pendReq')}</b>
            <span className={s.count}>{pend.length}</span>
            <span className={s.grow} />
            <button type="button" className={s.link} onClick={goLeave}>{t('ov.all')}</button>
          </div>
          <div className={s.list}>
            {pend.length === 0 && <div className={s.none}>{t('ov.noReq')}</div>}
            {pend.map((r) => (
              <div key={r.id} className={s.item}>
                <TypeIcon type={r.type} />
                <span className={s.main}>
                  <b>{rangeLabel(r)}</b>
                  <span>{qtyLabel(r, otHours(r))} · {r.sub}</span>
                </span>
                <Chip tone="warn" dot="var(--d-amber)" label={t('ov.waiting', { a: me.route[0]![0], b: me.route[0]![1] })}>
                  {me.route[0]![0]}
                </Chip>
              </div>
            ))}
          </div>
        </section>}
        <section className={s.card} aria-label={t('meeov.todo')}>
          <div className={s.head}>
            <b>{t('meeov.todo')}</b>
            <span className={s.count}>{todo.length}</span>
          </div>
          <div className={s.list}>
            {todo.length === 0 && <div className={s.none}>{t('ov.noTodo')}</div>}
            {todo.map((it) => {
              const Ico = it.kind === 'clock' ? IconClock : TODO_ICON[it.kind]!;
              return (
                <button key={it.k} type="button" className={`${s.item} ${s.itemBtn}`} onClick={() => toast({ msg: t('ov.noTodoData') })} aria-label={it.badge ? `${it.k} ${it.badge}` : it.k}>
                  <span className={s.ic} data-tone={it.badge ? it.tone : undefined}><Ico size={18} stroke={1.8} /></span>
                  <span className={s.main}><b>{it.k}</b></span>
                  {it.badge && (
                    <Chip tone={it.tone as 'warn' | 'err'} icon={it.badge.startsWith('−') || it.badge.endsWith('ngày') ? <IconAlarm size={14} /> : undefined} label={it.tip}>
                      {it.badge}
                    </Chip>
                  )}
                </button>
              );
            })}
          </div>
        </section>
        {!phone && <section className={s.card} aria-label={t('ov.holidays')}>
          <div className={s.head}>
            <b>{t('ov.holidays')}</b>
          </div>
          <div className={s.list}>
            {holidays.length === 0 && <div className={s.none}>{t('ov.noHol')}</div>}
            {holidays.map((h) => (
              <div key={h.from} className={s.item}>
                <span className={s.date}><b>{h.from.slice(8, 10)}</b><span>{monthAbbr(h.from)}</span></span>
                <span className={s.main}>
                  <b>{h.name}</b>
                  {!phone && <span>{t('ov.days', { n: h.days })}{h.company ? t('ov.companyOff') : ''}</span>}
                </span>
                <Chip icon={<IconCalendarEvent size={14} />} label={h.inDays === 0 ? t('ov.ongoing') : t('ov.inDays', { n: h.inDays })}>
                  {h.inDays === 0 ? t('ov.now') : t('ov.days', { n: h.inDays })}
                </Chip>
              </div>
            ))}
          </div>
        </section>}
      </div>

      <section className={s.card} aria-label={t('ov.adv')}>
        <div className={s.head}>
          <b>{t('ov.adv')}</b>
          <span className={s.grow} />
          <Button onClick={() => toast({ msg: t('ov.noEmp') })}>{t('ov.emp')} · {ADVANCE_SAMPLE.emp}</Button>
        </div>
        <div className={s.advKpis}>
          {ADVANCE_SAMPLE.kpis.map((k) => (
            <div key={k.k} className={s.tile} title={k.tip}>
              <span>{k.k}</span>
              <b data-tone={k.tone}>{k.v}</b>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}


export function OverviewScreen() {
  const t = useT();
  const m = useMee();
  const [create, setCreate] = useState(false);
  const [density, setDensity] = useState<'std' | 'compact'>('std');
  const nav = useNavigate();
  // Board `Mint-Mee` (state=…): đầu trang giữ Chuẩn·Gọn + chuông ở mọi trạng thái; "Tạo đơn" chỉ khi không loading / noperm.
  const canCreate = m.state === 'ready' || m.state === 'empty' || m.state === 'error';
  return (
    <>
      <PageHeader
        title={t('ov.title')}
        actions={
          <>
            <MeeHeaderTools density={density} onDensity={setDensity} pending={m.team.filter((r) => r.status === 'Pending').length} />
            {canCreate && <Button variant="primary" icon={<IconPlus size={18} />} onClick={() => setCreate(true)}>{t('ov.create')}</Button>}
          </>
        }
      />
      {m.state === 'noperm' ? (
        <NopermView />
      ) : (
        <StateCard state={m.state}>
        <StateView
          state={m.state}
          icon={m.state === 'empty' ? <IconId size={26} stroke={1.8} /> : undefined}
          title={m.state === 'empty' ? t('ov.empty') : m.state === 'error' ? t('ov.err') : undefined}
          desc={
            m.state === 'empty'
              ? t('ov.emptyDesc')
              : m.state === 'error'
                ? t('ov.errDesc')
                : undefined
          }
          action={
            m.state === 'error' ? (
              <Button icon={<IconPlayerPlay size={16} />} onClick={m.retry}>{t('ov.retry')}</Button>
            ) : m.state === 'empty' ? (
              <Button variant="primary" icon={<IconClock size={16} />} onClick={() => nav({ to: '/mee/time' })}>{t('ov.clockIn')}</Button>
            ) : undefined
          }
          skeleton={<OverviewSkeleton />}
        >
          <OverviewBody m={m} compact={density === 'compact'} />
          <RequestDrawer open={create} onOpenChange={setCreate} m={m} />
        </StateView>
        </StateCard>
      )}
    </>
  );
}
